import { useEffect, useState, useCallback } from "react";
import { LogIn, LogOut, Mail, Phone, Trash2, CheckCircle2, Inbox, RefreshCw } from "lucide-react";
import { logo } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import AdminAnalytics from "../components/AdminAnalytics";
import AdminChat from "../components/AdminChat";
import useChatUnread from "../hooks/useChatUnread";
import { BarChart3, Inbox as InboxIcon, MessageCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ct_admin_token";

const formatDetail = (d) => (Array.isArray(d) ? d.map((e) => e.msg).join(" ") : d || "Något gick fel");

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(formatDetail(data.detail));
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-white rounded-2xl border border-slate-100 shadow-xl p-8">
        <img src={logo} alt="City Trafikskola Uppsala" className="h-14 w-auto mb-6" />
        <h1 className="font-display text-2xl font-bold text-[#0B2545]">Admin</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">Logga in för att se inkomna meddelanden.</p>
        <label className="block text-sm font-semibold text-slate-600 mb-1.5">E-post</label>
        <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none transition mb-4" />
        <label className="block text-sm font-semibold text-slate-600 mb-1.5">Lösenord</label>
        <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none transition" />
        {error && <p data-testid="admin-login-error" className="text-sm text-rose-600 mt-3">{error}</p>}
        <button data-testid="admin-login-submit" disabled={loading} className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#0B2545] text-white font-semibold hover:bg-[#13315C] transition-colors disabled:opacity-60">
          <LogIn size={17} /> {loading ? "Loggar in…" : "Logga in"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const { toast } = useToast();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("statistik");

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMessages([]);
  }, []);

  const chatUnread = useChatUnread(token, logout);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/messages`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return logout();
      setMessages(await res.json());
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, method, path = "") => {
    const res = await fetch(`${API}/api/admin/messages/${id}${path}`, { method, headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) load();
    else toast({ title: "Något gick fel", variant: "destructive" });
  };

  if (!token) return <main className="pt-20 md:pt-[8.25rem] bg-[#f6f9fc]"><LoginForm onLogin={setToken} /></main>;

  const unread = messages.filter((m) => !m.read).length;

  return (
    <main className="pt-20 md:pt-[8.25rem] min-h-screen bg-[#f6f9fc]" data-testid="admin-dashboard">
      <section className="bg-[#0B2545] py-12">
        <div className="max-w-5xl mx-auto px-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-[#ffce3a] font-semibold tracking-[0.2em] text-xs uppercase">Admin</span>
            <h1 className="font-display text-3xl font-extrabold text-white mt-1">Adminpanel</h1>
            <p className="text-slate-300 text-sm mt-1" data-testid="admin-unread-count">{unread} olästa meddelanden · {messages.length} totalt</p>
            <div className="mt-4 inline-flex p-1 rounded-xl bg-white/10" role="tablist">
              <button onClick={() => setTab("statistik")} data-testid="admin-tab-statistik" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === "statistik" ? "bg-[#ffce3a] text-[#0B2545]" : "text-white hover:bg-white/10"}`}><BarChart3 size={15} /> Besöksstatistik</button>
              <button onClick={() => setTab("meddelanden")} data-testid="admin-tab-meddelanden" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === "meddelanden" ? "bg-[#ffce3a] text-[#0B2545]" : "text-white hover:bg-white/10"}`}><InboxIcon size={15} /> Meddelanden {unread > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px]">{unread}</span>}</button>
              <button onClick={() => setTab("chatt")} data-testid="admin-tab-chatt" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === "chatt" ? "bg-[#ffce3a] text-[#0B2545]" : "text-white hover:bg-white/10"}`}><MessageCircle size={15} /> Livechatt {chatUnread > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] animate-pulse" data-testid="admin-chat-tab-badge">{chatUnread}</span>}</button>
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-refresh-btn" onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 text-white text-sm font-semibold hover:bg-white/20 transition-colors"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Uppdatera</button>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#ffce3a] text-[#0B2545] text-sm font-semibold hover:bg-[#ffd85f] transition-colors"><LogOut size={15} /> Logga ut</button>
          </div>
        </div>
      </section>

      {tab === "statistik" && (
        <section className="max-w-6xl mx-auto px-5 py-10">
          <AdminAnalytics token={token} onUnauthorized={logout} />
        </section>
      )}

      {tab === "chatt" && (
        <section className="max-w-6xl mx-auto px-5 py-10">
          <AdminChat token={token} onUnauthorized={logout} />
        </section>
      )}

      <section className={`max-w-5xl mx-auto px-5 py-10 space-y-4 ${tab === "meddelanden" ? "" : "hidden"}`}>
        <p className="text-xs text-slate-500 flex items-center gap-2" data-testid="messages-retention-note"><CheckCircle2 size={14} className="text-emerald-500" /> Meddelanden äldre än 12 månader raderas automatiskt enligt integritetspolicyn.</p>
        {messages.length === 0 && !loading && (
          <div data-testid="admin-empty-state" className="text-center py-20 text-slate-400"><Inbox size={40} className="mx-auto mb-3" />Inga meddelanden ännu.</div>
        )}
        {messages.map((m) => (
          <article key={m.id} data-testid={`admin-message-${m.id}`} className={`bg-white rounded-2xl border p-6 ${m.read ? "border-slate-100" : "border-[#ffce3a] shadow-lg shadow-amber-100/50"}`}>
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  {!m.read && <span className="w-2 h-2 rounded-full bg-[#ffce3a]" />}
                  <h2 className="font-display font-bold text-lg text-[#0B2545]">{m.name}</h2>
                </div>
                <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-500">
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:text-[#1B6CA8]"><Mail size={14} /> {m.email}</a>
                  {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#1B6CA8]"><Phone size={14} /> {m.phone}</a>}
                </div>
              </div>
              <time className="text-xs text-slate-400 whitespace-nowrap">{new Date(m.created_at).toLocaleString("sv-SE")}</time>
            </div>
            <p className="mt-4 text-slate-700 whitespace-pre-wrap leading-relaxed">{m.message}</p>
            <div className="mt-5 flex gap-2">
              {!m.read && (
                <button data-testid={`admin-mark-read-${m.id}`} onClick={() => act(m.id, "PATCH", "/read")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f0f5fb] text-[#0B2545] text-sm font-semibold hover:bg-[#e2ecf7] transition-colors"><CheckCircle2 size={15} /> Markera som läst</button>
              )}
              <button data-testid={`admin-delete-${m.id}`} onClick={() => act(m.id, "DELETE")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-rose-600 text-sm font-semibold hover:bg-rose-50 transition-colors"><Trash2 size={15} /> Ta bort</button>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
